import { getEnv, getCsrfToken } from '@ctct/ctct-helpers';

import type { AddOnResponseItem, CancelAddOnSubscriptionBody } from './types/add-ons.types';
import type { AmazonPaySignaturePayload } from './types/amazon-pay.types';
import type { BillingAccountResponse, BillingCustomersResponse } from './types/billing.types';
import type { OnlineCancelBody } from './types/cancel.types';
import type {
  OverduePaymentPayload,
  PaymentPayload,
  PaymentResponse,
  UpdatePaymentPlanBody,
} from './types/payment.types';
import type { RatingResponse, RatingPayload } from './types/rating-call.types';
import { BaseApiClient } from '../BaseApiClient';

class MyAccountApi {
  private baseApiClient: BaseApiClient;

  private baseUrl: string;

  constructor() {
    this.baseUrl = `https://app${getEnv()}constantcontact.com/v1/myaccountapi`;
    this.baseApiClient = new BaseApiClient(this.baseUrl);
  }

  public getBillingInfo() {
    return this.baseApiClient.get<unknown>('/billing/customers/users/billinginfo');
  }

  public putBillingInfo(payload: unknown) {
    return this.baseApiClient.put<unknown, unknown>('/billing/customers/users/billinginfo', payload);
  }

  public getBillingAccount() {
    return this.baseApiClient.get<BillingAccountResponse>('/billing/customers/account');
  }

  public getCustomer() {
    return this.baseApiClient.get<BillingCustomersResponse>('/billing/customers');
  }

  public getCountries() {
    return this.baseApiClient.get<unknown>('/geo/countries');
  }

  public getAddOns(planGroupId: number, planId: number, currencyCode: string, includePurchased: boolean) {
    return this.baseApiClient.get<AddOnResponseItem[]>(
      `/plan/customers/addons/plangroups/${planGroupId}/plans/${planId}`,
      {
        includePurchased: includePurchased ? 'true' : undefined,
        currencyCode,
      }
    );
  }

  public getAmazonPayCheckoutSession(amazonCheckoutSessionId: string) {
    return this.baseApiClient.get<unknown>(`/billing/customers/amazonpay/checkoutSession/${amazonCheckoutSessionId}`);
  }

  public getRMCValue(key: string) {
    return this.baseApiClient.get<unknown>(`/billing/utils/${key}/configurationvalue`);
  }

  public getSalesforceAccountData() {
    return this.baseApiClient.get<unknown>('/billing/customers/salesforce');
  }

  public getPaymentPlan() {
    return this.baseApiClient.get<unknown>('/billing/customers/paymentplan');
  }

  public getPlanOverview() {
    return this.baseApiClient.get<unknown>('/plan/customers/users/overview');
  }

  public getOfferCodes() {
    return this.baseApiClient.get<unknown>('/offers/customers/offercodes');
  }

  public getNonProfitStatuses() {
    return this.baseApiClient.get<unknown>('/billing/customers/users/nonprofit_upload');
  }

  public getNonProfitDownloadFile(filename: string) {
    return this.baseApiClient.get('/billing/customers/users/nonprofit_download', { filename });
  }

  public postUploadNonProfitFile(formData: FormData) {
    const token = getCsrfToken();

    return window.fetch(`${this.baseUrl}/billing/customers/users/nonprofit_upload`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'follow',
      headers: {
        'X-CSRF-Token': token,
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
  }

  public getPlanGroup(currency: string, count = 0, usage?: string, planGroupId?: number) {
    // usage should only be passed if account is more than 5 minutes old.
    return this.baseApiClient.get<unknown>(`/plan/customers/users/currency/${currency}/plan_group`, {
      count,
      usage,
      planGroupId,
    });
  }

  public getBalance() {
    return this.baseApiClient.get<unknown>('/billing/customers/users/balance');
  }

  public getPublishableKey() {
    return this.baseApiClient.get<{ key: string }>('/billing/publishable-key');
  }

  public postPlanRating(body: RatingPayload) {
    return this.baseApiClient.post<RatingResponse, RatingPayload>('/billing/customers/plan/rating', body);
  }

  public postCreatePaypalToken(body: Record<string, unknown>) {
    return this.baseApiClient.post('/billing/customers/users/paypaltoken', body);
  }

  public putPaypalAgreementToken(token: string) {
    return this.baseApiClient.put('/billing/customers/users/paypalagreement', { token });
  }

  public postPayment(body: PaymentPayload) {
    return this.baseApiClient.post<PaymentResponse, PaymentPayload>('/billing/customers/users/plan/pay', body);
  }

  public putOverdueBalancePayment(body: OverduePaymentPayload) {
    return this.baseApiClient.post<PaymentResponse, OverduePaymentPayload>('/billing/customers/payment', body);
  }

  public postCreateAmazonPaySignature(body: AmazonPaySignaturePayload) {
    return this.baseApiClient.post<string, { payload: string }>('/billing/customers/amazonpay/signature', {
      payload: JSON.stringify(body),
    });
  }

  public postSetPendingSuspend(suspendReason: string) {
    return this.baseApiClient.post<unknown, string>('/billing/customers/setpendingsuspend', suspendReason);
  }

  public postCancelAddOnSubscription(body: CancelAddOnSubscriptionBody) {
    return this.baseApiClient.post<unknown, unknown>('/billing/customers/canceladdonsubscription', body);
  }

  public postUpdatePaymentPlan(body: UpdatePaymentPlanBody) {
    return this.baseApiClient.post<unknown, unknown>('/billing/customers/paymentplan', body);
  }

  public getAvailableCurrencies() {
    return this.baseApiClient.get<unknown>('/billing/customers/availablecurrency');
  }

  public postUpdateCurrency(currencyCode: string) {
    return this.baseApiClient.post<unknown, string>('/billing/customers/currency', currencyCode, undefined, {
      // axios is automatically wrapping string in double quotes,
      // we need to overwrite this behaviour to send plain string which is required by this endpoint
      transformRequest: (requestData) => requestData,
    });
  }

  public postOnlineCancel(data: OnlineCancelBody, updateBy = 'customer') {
    return this.baseApiClient.post<unknown, unknown>(`/billing/customers/online_cancel`, data, {
      updateBy,
    });
  }

  public postOnlineCancelVoid(updateBy = 'customer') {
    return this.baseApiClient.post<unknown, unknown>(
      '/billing/customers/online_cancel/void',
      {},
      {
        updateBy,
      }
    );
  }

  /**
   * These values are returned so they can be entered into Salesforce, but we
   * never want to display them to the customer.
   *
   * @see https://ctct.atlassian.net/browse/CPD-7208 Refer to "Issue 4" in the description
   */
  public getHiddenSalesforceFields() {
    return {
      lastname: 'LastNameBN',
      phone: '2222222222',
      companyname: 'Company or Organization Name',
    };
  }

  public parseHiddenFields({ lastname, phone, companyname }: { lastname: string; phone: string; companyname: string }) {
    const hiddenFields = this.getHiddenSalesforceFields();

    return {
      lastname: lastname === hiddenFields.lastname ? '' : lastname,
      phone: phone.includes(hiddenFields.phone) ? '' : phone,
      companyname: companyname === hiddenFields.companyname ? '' : companyname,
    };
  }
}

export const myAccountApi = new MyAccountApi();
